/**
 * Instagram feed accessibility correction.
 *
 * The Smash Balloon "Instagram Feed Pro" plugin renders its feed container
 * (`#sbi_images`) server-side without a role, then -- in carousel layout --
 * its front-end script applies `role="carousel"` to that container at runtime
 * (see instagram-feed-pro/js/sb-instagram.js). `carousel` is not a valid ARIA
 * role, so assistive technology mis-announces the region (axe: aria-roles,
 * Critical). The same script also leaves non-descriptive lightbox anchors.
 *
 * The plugin (6.9.0) has no update available and exposes no PHP filter on the
 * feed container markup, so this correction has to happen client-side after the
 * plugin's own script runs. We simply REMOVE the invalid `role` (and any
 * `aria-roledescription` an earlier build paired with it), leaving a plain
 * container whose Instagram post links remain individually accessible. We
 * deliberately do not substitute `role="list"`: in the carousel layout the
 * container's direct children are the carousel's stage/navigation wrappers, not
 * list items, so `role="list"` would trade the invalid-role defect for an
 * `aria-required-children` violation. Removing the bogus role clears both.
 *
 * The module is defensive: it does nothing when no feed is present, and it
 * watches for the role being (re)applied because the plugin sets it
 * asynchronously after the feed's images load.
 *
 * @since 1.0.0
 */

const INVALID_ROLES = [ 'carousel' ];

/**
 * Correct the role attribute on a single feed container.
 *
 * Only touches containers whose role is one of the known-invalid values, so
 * re-running this (e.g. from a MutationObserver) is idempotent and never
 * clobbers a valid author-supplied role. The invalid role is removed outright
 * rather than replaced, so no `aria-required-children` constraint is imposed.
 *
 * @since 1.0.0
 *
 * @param {Element} container - The `#sbi_images` feed container element.
 *
 * @returns {void}
 */
function correctFeedRole( container ) {
	if ( ! container ) {
		return;
	}

	const role = container.getAttribute( 'role' );

	if ( role && INVALID_ROLES.includes( role.toLowerCase() ) ) {
		// Remove the invalid role entirely. A plain container leaves the feed's
		// post links individually accessible without imposing list semantics
		// the carousel markup cannot satisfy.
		container.removeAttribute( 'role' );

		// Drop a roledescription only meaningful alongside a role (paired with
		// the role by an earlier build); without a role it is itself invalid.
		container.removeAttribute( 'aria-roledescription' );
	}
}

/**
 * Watch a feed container for the plugin (re)applying an invalid role.
 *
 * The plugin sets `role="carousel"` after the feed initializes, which can be
 * after this module first runs. A scoped attribute observer corrects it as soon
 * as it appears, then keeps correcting if the plugin re-renders.
 *
 * @since 1.0.0
 *
 * @param {Element} container - The `#sbi_images` feed container element.
 *
 * @returns {void}
 */
function observeFeed( container ) {
	if ( ! container || typeof MutationObserver === 'undefined' ) {
		return;
	}

	correctFeedRole( container );

	const observer = new MutationObserver( () => correctFeedRole( container ) );

	observer.observe( container, {
		attributes: true,
		attributeFilter: [ 'role' ],
	} );
}

/**
 * Initialize the Instagram feed accessibility correction.
 *
 * Bails out entirely when there is no Smash Balloon feed on the page so the
 * module is a no-op everywhere except where the feed renders.
 *
 * @since 1.0.0
 *
 * @returns {void}
 */
export function init() {
	if ( typeof document === 'undefined' ) {
		return;
	}

	const containers = document.querySelectorAll( '#sbi_images, .sbi_images' );

	if ( ! containers.length ) {
		return;
	}

	containers.forEach( observeFeed );
}

init();
